import { useGreeting } from "@atlas/composite";
import { Avatar, AvatarFallback, Typography, useToast } from "@atlas/core";
import { useTranslation } from "@atlas/sdk";
import { useAppLaunch, useCompany, useUserInfo } from "@query";
import {
  APP_BASE_PATH,
  getAppLaunchFailureMeta,
  getIconUrl,
  isNoLaunchGroupsMessage,
  openLaunchedApplicationHtml,
} from "@shared";
import { Building2 } from "lucide-react";
import { useCallback, useMemo } from "react";
import { useForm, useWatch } from "react-hook-form";
import { getUniqueHomeApplicationNames } from "../homeLaunchpadFilters.utils";
import { useHomeLaunchpadApplications } from "../hooks/useHomeLaunchpadApplications";
import { useHomeLaunchpadFilterTable } from "../hooks/useHomeLaunchpadFilterTable";
import { HomeLaunchpadContent } from "./HomeLaunchpadContent";
import {
  HomeLaunchpadToolbar,
  type IHomeLaunchpadToolbarFormValues,
} from "./HomeLaunchpadToolbar";

export function Home() {
  const { t } = useTranslation();
  const toast = useToast();
  const { data: userInfo, isLoading: userInfoLoading } =
    useUserInfo(APP_BASE_PATH);
  const companyUuid = userInfo?.companyUuid;
  const { data: company, isLoading: companyLoading } = useCompany(
    companyUuid,
    APP_BASE_PATH,
  );
  const {
    mutateAsync: appLaunch,
    isPending,
    variables: launchVariables,
  } = useAppLaunch(APP_BASE_PATH);

  const handleAppLaunch = useCallback(
    async (launchUrl: string, solutionId?: string, appMapId?: string) => {
      if (!launchUrl) return;
      try {
        const htmlContent = solutionId
          ? await appLaunch({ launchUrl, solutionId })
          : await appLaunch({ launchUrl, appMapId });
        openLaunchedApplicationHtml(htmlContent);
      } catch (err) {
        console.error("App launch failed:", err);
        const { httpStatus, message } = getAppLaunchFailureMeta(err);
        const noGroups = httpStatus === 500 && isNoLaunchGroupsMessage(message);
        toast.add({
          title: noGroups
            ? t("hub.launch.error_no_groups")
            : t("hub.launch.error_launch_failed"),
          description: noGroups ? undefined : message || undefined,
          data: { type: "error" },
        });
      }
    },
    [appLaunch, t, toast],
  );

  const welcomeName = useMemo(() => {
    const first = userInfo?.firstName?.trim() ?? "";
    return first || userInfo?.loginId || t("hub.launch.welcome_name_fallback");
  }, [userInfo?.firstName, userInfo?.loginId, t]);

  const userName = useMemo(
    () =>
      [userInfo?.firstName, userInfo?.lastName]
        .filter(Boolean)
        .join(" ")
        .trim(),
    [userInfo?.firstName, userInfo?.lastName],
  );

  const companyLogoUrl = useMemo(
    () => (company?.logo ? getIconUrl(company.logo) : ""),
    [company?.logo],
  );

  const greeting = useGreeting();
  const { control } = useForm<IHomeLaunchpadToolbarFormValues>({
    defaultValues: {
      searchQuery: "",
    },
  });
  const searchQuery = useWatch({ control, name: "searchQuery" });
  const { tenants, recentlyUsedSolutions, isLoading } =
    useHomeLaunchpadApplications({
      companyUuid,
      userUuid: userInfo?.id,
      enabled: !!userInfo,
      userInfoLoading,
      companyLoading,
    });

  const solutionsFetchEnabled = !!userInfo;

  const availableApplicationNames = useMemo(
    () => getUniqueHomeApplicationNames(tenants),
    [tenants],
  );

  const {
    table: filterTable,
    filters: filterConfig,
    selectedApplicationNames,
    activeFilterCount,
    selectApplicationFilter,
  } = useHomeLaunchpadFilterTable(t, availableApplicationNames);

  return (
    <div className="flex h-full min-h-0 min-w-0 flex-1 flex-col gap-4 text-left">
      <div className="flex h-full min-h-0 min-w-0 flex-col gap-6 overflow-hidden">
        <div className="mx-auto box-border flex w-full max-w-[90rem] flex-col gap-4 pt-1">
          {company ? (
            <div className="shrink-0 p-(--spacing-2)">
              {companyLogoUrl ? (
                <img
                  src={companyLogoUrl}
                  alt={company.name ?? ""}
                  className="block h-auto max-h-[25px] w-auto object-contain"
                />
              ) : (
                <div className="flex items-center gap-2">
                  <Avatar className="size-[30px] shrink-0">
                    <AvatarFallback>
                      <Building2 className="size-8 text-muted-foreground" />
                    </AvatarFallback>
                  </Avatar>
                  <Typography.H4 className="truncate">
                    {company.name}
                  </Typography.H4>
                </div>
              )}
            </div>
          ) : null}
          {userInfo || company ? (
            <div className="flex shrink-0 flex-col gap-1">
              <div className="font-bold text-2xl text-foreground">
                {greeting}, <span className="capitalize">{welcomeName}</span>!
              </div>
              <Typography.Muted>
                {t("hub.home.welcome_subtitle", {
                  defaultValue:
                    "Welcome to Iron Mountain Insight Portal! Choose an application below and click Launch to get started.",
                })}
              </Typography.Muted>
            </div>
          ) : (
            <div />
          )}
        </div>

        <div className="mx-auto box-border flex w-full max-w-[90rem] flex-col gap-4">
          <HomeLaunchpadToolbar
            control={control}
            selectedApplicationNames={selectedApplicationNames}
            onApplicationFilterChange={selectApplicationFilter}
            availableApplicationNames={availableApplicationNames}
            filterTable={filterTable}
            filterConfig={filterConfig}
            activeFilterCount={activeFilterCount}
          />
        </div>

        <HomeLaunchpadContent
          tenants={tenants}
          recentlyUsedSolutions={recentlyUsedSolutions}
          isLoading={isLoading}
          companyUuid={companyUuid}
          solutionsFetchEnabled={solutionsFetchEnabled}
          isLaunchPending={isPending}
          launchVariables={launchVariables}
          searchQuery={searchQuery}
          selectedApplicationNames={selectedApplicationNames}
          onLaunchApp={handleAppLaunch}
          userType={userInfo?.userType}
          userName={userName}
          userEmail={userInfo?.email ?? ""}
          companyName={company?.name ?? ""}
          isInternalUser={userInfo?.isInternal ?? false}
          isIRMDomain={userInfo?.isIRMDomain ?? false}
        />
      </div>
    </div>
  );
}
